/**
 * Mock exchange rates — PROTOTYPE DATA, not real market rates.
 * Copied from the fx-currency-expenses prototype (AUD added, since the currency
 * dropdown offers it).
 *
 * Stands in for whatever rate source the backend would use. Everything that
 * needs a rate (the mock API when it stores an expense, the form when it
 * previews the conversion before saving) goes through this one function, so the
 * number a reviewer sees in the drawer is the number that gets stored.
 *
 * A rate is quoted as "1 {currency} = {rate} USD" and depends on the expense
 * date: the base rate is nudged by a deterministic daily drift, so moving the
 * date by a day visibly moves both the rate and the converted amount — which is
 * the whole point of showing the date the rate was taken on.
 */

/** The label currency everything converts to. */
export const LABEL_CURRENCY = "USD";

/** Rates are quoted and stored at 5 decimal places. */
export const RATE_DECIMALS = 5;

/** Converted amounts, like every other amount, at 2. */
export const AMOUNT_DECIMALS = 2;

/** 1 unit of the currency in USD, before the daily drift. */
const BASE_RATES: Record<string, number> = {
  USD: 1,
  AUD: 0.65803,
  CAD: 0.71815,
  EUR: 1.159,
  GBP: 1.3401,
};

/** Keeps each currency's drift on its own curve instead of all moving together. */
const PHASE: Record<string, number> = {
  AUD: 19,
  CAD: 0,
  EUR: 37,
  GBP: 71,
};

/** Whole days since the epoch, read from a YYYY-MM-DD string with no timezone shift. */
function dayNumber(isoDate: string): number {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return 0;
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * A small, smooth, repeatable wander around the base rate (about ±1.3%), built
 * from two sine waves so consecutive days differ by roughly a tenth of a
 * percent — enough to read at 5 decimal places and to move a converted amount
 * at 2.
 */
function dailyDrift(currency: string, isoDate: string): number {
  const day = dayNumber(isoDate) + (PHASE[currency] ?? 0);
  return 0.009 * Math.sin(day / 9.3) + 0.004 * Math.sin(day / 2.7);
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Currencies this prototype can quote. */
export function isSupportedCurrency(currency: string | null | undefined): boolean {
  return !!currency && currency in BASE_RATES;
}

/**
 * The rate for one unit of `currency` in USD on `isoDate`.
 * Returns 1 for the label currency, null for anything unquoted.
 */
export function exchangeRateFor(
  currency: string | null | undefined,
  isoDate: string | null | undefined,
): number | null {
  if (!currency || !isSupportedCurrency(currency)) return null;
  if (currency === LABEL_CURRENCY) return 1;
  if (!isoDate) return null;
  return roundTo(BASE_RATES[currency] * (1 + dailyDrift(currency, isoDate)), RATE_DECIMALS);
}

export interface Conversion {
  /** 1 {currency} = {rate} USD, at RATE_DECIMALS. */
  rate: number;
  /** The amount in USD, at AMOUNT_DECIMALS. */
  converted: number;
}

/**
 * Convert an amount to the label currency at the rate for that date.
 * Null when there is nothing to convert (no amount, no date, or no rate).
 */
export function convertToLabelCurrency(
  amount: number | null | undefined,
  currency: string | null | undefined,
  isoDate: string | null | undefined,
): Conversion | null {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return null;
  const rate = exchangeRateFor(currency, isoDate);
  if (rate === null) return null;
  return { rate, converted: roundTo(amount * rate, AMOUNT_DECIMALS) };
}
